import { Tables, putItem, scanAll, getItem, updateItem } from "./db";
import type { BugReport, ItemStatus, ItemPriority } from "../types";

/** Get all bug reports */
export async function getAllBugs(): Promise<BugReport[]> {
  const items = await scanAll(Tables.BUGS);
  return (items as BugReport[]).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Get a single bug report */
export async function getBug(id: string): Promise<BugReport | undefined> {
  const item = await getItem(Tables.BUGS, { id });
  return item as BugReport | undefined;
}

/** Create a new bug report */
export async function createBug(data: {
  title: string;
  description: string;
  priority: ItemPriority;
  severity: BugReport["severity"];
  author: string;
  stepsToReproduce?: string;
}): Promise<BugReport> {
  const bug: BugReport = {
    id: `bug-${Date.now()}`,
    title: data.title,
    description: data.description,
    status: "open",
    priority: data.priority,
    severity: data.severity,
    createdAt: new Date().toISOString(),
    linkedCommits: [],
    author: data.author,
    stepsToReproduce: data.stepsToReproduce,
  };

  await putItem(Tables.BUGS, bug);
  return bug;
}

/** Update a bug report */
export async function updateBug(
  id: string,
  updates: Partial<Pick<BugReport, "title" | "description" | "status" | "priority" | "severity" | "fixedInVersion" | "linkedCommits" | "stepsToReproduce">>
): Promise<BugReport | undefined> {
  const existing = await getBug(id);
  if (!existing) return undefined;

  const merged = { ...updates, updatedAt: new Date().toISOString() };
  await updateItem(Tables.BUGS, { id }, merged);
  return { ...existing, ...merged };
}

/** Link a commit to a bug */
export async function linkCommitToBug(bugId: string, commitId: string): Promise<void> {
  const bug = await getBug(bugId);
  if (!bug) return;

  if (!bug.linkedCommits.includes(commitId)) {
    await updateItem(Tables.BUGS, { id: bugId }, {
      linkedCommits: [...bug.linkedCommits, commitId],
      updatedAt: new Date().toISOString(),
    });
  }
}

/** Mark a bug as fixed in a version */
export async function fixBugInVersion(bugId: string, versionId: string): Promise<void> {
  await updateItem(Tables.BUGS, { id: bugId }, {
    status: "completed" as ItemStatus,
    fixedInVersion: versionId,
    updatedAt: new Date().toISOString(),
  });
}
